import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@roger/ui-components';
import {
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  Play,
  RotateCcw,
  Search,
  XCircle,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { preflightStediReplay, replayStediWebhooks } from '../../services/api';
import type {
  StediReplayPreflightRow,
  StediReplayResultRow,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { inferDeployStageLabel } from '../../utils/deployStage';

type PanelMode = 'idle' | 'preflight' | 'replay';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const ERROR_FALLBACK = 'Unable to reach the replay service. Try again.';

/** Split on any whitespace or comma, trim, drop empties. Order is kept. */
function parseIds(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map(part => part.trim())
    .filter(Boolean);
}

const VERDICT_LABELS: Record<StediReplayPreflightRow['verdict'], string> = {
  replayable: 'Replayable',
  already_stored: 'Already stored',
  in_flight: 'In flight',
  unknown_id: 'Unknown ID',
  not_835: 'Not 835',
};

const VERDICT_VARIANTS: Record<
  StediReplayPreflightRow['verdict'],
  BadgeVariant
> = {
  replayable: 'default',
  already_stored: 'secondary',
  in_flight: 'outline',
  unknown_id: 'outline',
  not_835: 'outline',
};

/** A batch of one is the common case here, so "1 transactions" would show often. */
const plural = (count: number, one: string, many: string): string =>
  count === 1 ? one : many;

const VERDICT_SENTENCES: Record<StediReplayPreflightRow['verdict'], string> = {
  replayable: 'Not stored. Replay will start the processor workflow.',
  // Not "does nothing": the storage-layer dedupe calls undelete() on a
  // soft-deleted remittance before returning created=False, and the activity
  // commits it. A replay therefore cannot create a second remittance, but it
  // CAN restore one that was deleted.
  already_stored:
    'Already stored. A replay will not create a second remittance, but it can restore a deleted one.',
  // "received" is both "still running" and "died before it could mark the row".
  // The database cannot tell them apart, so the operator has to decide.
  in_flight:
    'Still marked received. The original run may still be going — replaying now can race it. If it is stuck, replay is the fix.',
  unknown_id: 'No webhook event for this ID. Check the ID in Stedi.',
  not_835: 'This transaction type has no replay path.',
};

const VerdictBadge: React.FC<{
  verdict: StediReplayPreflightRow['verdict'];
}> = ({ verdict }) => {
  const icon =
    verdict === 'replayable' ? (
      <Play className='h-3 w-3 mr-1' />
    ) : verdict === 'already_stored' ? (
      <CheckCircle2 className='h-3 w-3 mr-1 text-green-600' />
    ) : verdict === 'in_flight' ? (
      <Loader2 className='h-3 w-3 mr-1 text-amber-600' />
    ) : verdict === 'unknown_id' ? (
      <HelpCircle className='h-3 w-3 mr-1 text-muted-foreground' />
    ) : (
      <AlertTriangle className='h-3 w-3 mr-1 text-amber-600' />
    );

  return (
    <Badge
      variant={VERDICT_VARIANTS[verdict]}
      className='inline-flex items-center whitespace-nowrap font-normal'
    >
      {icon}
      {VERDICT_LABELS[verdict]}
    </Badge>
  );
};

const OutcomeBadge: React.FC<{ outcome: StediReplayResultRow['outcome'] }> = ({
  outcome,
}) => (
  <Badge
    variant={outcome === 'started' ? 'default' : 'destructive'}
    className='inline-flex items-center whitespace-nowrap font-normal'
  >
    {outcome === 'started' ? (
      <Play className='h-3 w-3 mr-1' />
    ) : (
      <XCircle className='h-3 w-3 mr-1' />
    )}
    {outcome === 'started' ? 'Started' : 'Error'}
  </Badge>
);

const ConfirmReplayDialog: React.FC<{
  open: boolean;
  busy: boolean;
  count: number;
  typeBreakdown: string;
  alreadyStoredCount: number;
  inFlightCount: number;
  stageLabel: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}> = ({
  open,
  busy,
  count,
  typeBreakdown,
  alreadyStoredCount,
  inFlightCount,
  stageLabel,
  onOpenChange,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='max-w-md'>
      <DialogHeader>
        <DialogTitle>
          Replay {count} {plural(count, 'transaction', 'transactions')}?
        </DialogTitle>
      </DialogHeader>
      <div className='space-y-2 py-1 text-sm text-muted-foreground'>
        <p>
          This starts {count} processor {plural(count, 'workflow', 'workflows')}{' '}
          in {stageLabel}: {typeBreakdown}.
          {alreadyStoredCount > 0
            ? ` Includes ${alreadyStoredCount} already stored.`
            : ''}
        </p>
        {inFlightCount > 0 && (
          <p className='text-amber-600'>
            {inFlightCount} of these {plural(inFlightCount, 'is', 'are')} still
            marked received. If the original run is live, this replay races it.
          </p>
        )}
        <p>The result is not immediate. Press Preflight again to read it.</p>
      </div>
      <DialogFooter>
        <Button
          variant='outline'
          size='sm'
          disabled={busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          variant='destructive'
          size='sm'
          disabled={busy}
          onClick={onConfirm}
        >
          Replay
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const StediWebhookReplayPanel: React.FC = () => {
  const stageLabel = useMemo(() => inferDeployStageLabel(), []);
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [rawIds, setRawIds] = useState('');
  const [mode, setMode] = useState<PanelMode>('idle');
  const [preflightRows, setPreflightRows] = useState<
    StediReplayPreflightRow[] | null
  >(null);
  const [replayRows, setReplayRows] = useState<StediReplayResultRow[] | null>(
    null
  );
  const [isPreflighting, setIsPreflighting] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [includeAlreadyStored, setIncludeAlreadyStored] = useState(false);
  const [includeInFlight, setIncludeInFlight] = useState(false);

  const pastedIds = useMemo(() => parseIds(rawIds), [rawIds]);
  const transactionIds = useMemo(
    () => Array.from(new Set(pastedIds)),
    [pastedIds]
  );
  const duplicateCount = pastedIds.length - transactionIds.length;

  const busy = isPreflighting || isReplaying;

  const hasAlreadyStored = (preflightRows ?? []).some(
    row => row.verdict === 'already_stored'
  );
  const hasInFlight = (preflightRows ?? []).some(
    row => row.verdict === 'in_flight'
  );

  const replayableRows = useMemo(
    () =>
      (preflightRows ?? []).filter(
        row =>
          row.verdict === 'replayable' ||
          (includeAlreadyStored && row.verdict === 'already_stored') ||
          (includeInFlight && row.verdict === 'in_flight')
      ),
    [preflightRows, includeAlreadyStored, includeInFlight]
  );
  const replayableIds = useMemo(
    () => replayableRows.map(row => row.transaction_id),
    [replayableRows]
  );

  const canPreflight = transactionIds.length > 0 && !busy;
  const canReplay = replayableIds.length > 0 && !busy;

  const typeBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    replayableRows.forEach(row => {
      const key = row.transaction_set_identifier ?? 'unknown type';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([type, count]) => `${count} × ${type}`)
      .join(', ');
  }, [replayableRows]);

  const alreadyStoredSelectedCount = replayableRows.filter(
    row => row.verdict === 'already_stored'
  ).length;

  const inFlightSelectedCount = replayableRows.filter(
    row => row.verdict === 'in_flight'
  ).length;

  const startedCount = (replayRows ?? []).filter(
    row => row.outcome === 'started'
  ).length;
  const replayHadError = (replayRows ?? []).some(
    row => row.outcome === 'error'
  );

  const handleRawIdsChange = (value: string) => {
    setRawIds(value);
    setPreflightRows(null);
    setReplayRows(null);
    setIncludeAlreadyStored(false);
    setIncludeInFlight(false);
    setMode('idle');
  };

  const runPreflight = async () => {
    setIsPreflighting(true);
    setErrorMessage(null);
    try {
      const res = await preflightStediReplay({
        body: { transaction_ids: transactionIds },
        throwOnError: true,
      });
      setPreflightRows(res.data.rows);
      setReplayRows(null);
      setIncludeAlreadyStored(false);
      setMode('preflight');
    } catch (error) {
      console.error('Stedi replay preflight failed', error);
      const message = getBusinessErrorMessage(error) ?? ERROR_FALLBACK;
      setErrorMessage(message);
      showError('Preflight failed', message);
    } finally {
      setIsPreflighting(false);
    }
  };

  const runReplay = async () => {
    setIsReplaying(true);
    setErrorMessage(null);
    try {
      const res = await replayStediWebhooks({
        body: {
          transaction_ids: replayableIds,
          // The server refuses a received row without this. Sending the
          // checkbox state keeps the deliberate opt-in end to end.
          include_in_flight: includeInFlight,
        },
        throwOnError: true,
      });
      setReplayRows(res.data.rows);
      setMode('replay');
      setConfirmOpen(false);
      // A 200 only means the batch was processed row by row. Every row can
      // still read "error", so the toast has to follow the rows, not the
      // status code -- a green "Replay started" over an all-failed batch is
      // exactly the wrong signal for a tool built to surface failures.
      const started = res.data.rows.filter(
        row => row.outcome === 'started'
      ).length;
      if (started === 0) {
        showError('No replays started', 'Review the rows below.');
      } else {
        showSuccess(
          `Started ${started} ${plural(started, 'replay', 'replays')}`,
          'Press Preflight again to read the result.'
        );
      }
    } catch (error) {
      console.error('Stedi replay failed', error);
      const message = getBusinessErrorMessage(error) ?? ERROR_FALLBACK;
      setErrorMessage(message);
      showError('Replay failed', message);
      setConfirmOpen(false);
    } finally {
      setIsReplaying(false);
    }
  };

  const preflightLabel = isPreflighting
    ? 'Checking…'
    : mode === 'replay'
      ? 'Preflight again'
      : 'Preflight';

  const replayLabel = isReplaying
    ? 'Starting…'
    : replayableIds.length > 0
      ? `Replay ${replayableIds.length}`
      : 'Replay';

  return (
    <section>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
          <RotateCcw className='h-4 w-4 shrink-0' />
          Re-drive a Stedi webhook that failed after delivery.
        </p>
        <Badge
          variant='outline'
          className='w-fit shrink-0 whitespace-nowrap font-mono'
        >
          {stageLabel}
        </Badge>
      </div>

      <div className='space-y-4'>
        <label className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Transaction IDs</span>
          <Textarea
            textareaSize='sm'
            rows={4}
            value={rawIds}
            disabled={busy}
            placeholder='Paste Stedi transaction IDs, one per line'
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleRawIdsChange(event.target.value)
            }
          />
        </label>

        {rawIds.trim().length > 0 && (
          <p className='text-xs text-muted-foreground'>
            {transactionIds.length === 0
              ? 'No transaction IDs found.'
              : `${transactionIds.length} ${plural(
                  transactionIds.length,
                  'ID',
                  'IDs'
                )} ready${
                  duplicateCount > 0
                    ? ` · ${duplicateCount} ${plural(
                        duplicateCount,
                        'duplicate',
                        'duplicates'
                      )} removed`
                    : ''
                }`}
          </p>
        )}

        <div className='flex flex-col gap-2 sm:flex-row sm:justify-end'>
          <Button
            className='w-full sm:w-auto'
            variant={mode === 'replay' ? 'default' : 'outline'}
            size='sm'
            disabled={!canPreflight}
            onClick={() => void runPreflight()}
          >
            {isPreflighting ? (
              <Loader2 className='h-4 w-4 mr-1 animate-spin' />
            ) : (
              <Search className='h-4 w-4 mr-1' />
            )}
            {preflightLabel}
          </Button>
          <Button
            className='w-full sm:w-auto'
            variant='destructive'
            size='sm'
            disabled={!canReplay}
            title={preflightRows === null ? 'Run Preflight first' : undefined}
            onClick={() => setConfirmOpen(true)}
          >
            {isReplaying ? (
              <Loader2 className='h-4 w-4 mr-1 animate-spin' />
            ) : (
              <Play className='h-4 w-4 mr-1' />
            )}
            {replayLabel}
          </Button>
        </div>

        {hasAlreadyStored && (
          <label className='flex items-start gap-2 text-sm cursor-pointer'>
            <Checkbox
              checkboxSize='sm'
              checked={includeAlreadyStored}
              disabled={busy}
              aria-label='Include already-stored transactions'
              onCheckedChange={(checked: boolean | 'indeterminate') =>
                setIncludeAlreadyStored(checked === true)
              }
              className='mt-0.5'
            />
            <span>
              Include already-stored transactions
              <span className='block text-xs text-muted-foreground'>
                An 835 can be partly stored. A replay is de-duplicated per
                remittance.
              </span>
            </span>
          </label>
        )}

        {hasInFlight && (
          <label className='flex items-start gap-2 text-sm cursor-pointer'>
            <Checkbox
              checkboxSize='sm'
              checked={includeInFlight}
              disabled={busy}
              aria-label='Include in-flight transactions'
              onCheckedChange={(checked: boolean | 'indeterminate') =>
                setIncludeInFlight(checked === true)
              }
              className='mt-0.5'
            />
            <span>
              Include in-flight transactions
              <span className='block text-xs text-muted-foreground'>
                Only when you know the original run is stuck. Replaying a live
                run can race it and leave a misleading status.
              </span>
            </span>
          </label>
        )}

        {isReplaying && replayableIds.length > 5 && (
          <p className='text-xs text-muted-foreground'>
            Starting workflows. This takes a few seconds.
          </p>
        )}

        <p className='text-xs text-muted-foreground'>
          Preflight reads only. Nothing changes until you press Replay.
        </p>

        {errorMessage && (
          <Alert className='border-red-200 bg-red-50'>
            <AlertDescription className='flex items-start gap-2 text-sm text-red-900'>
              <XCircle className='h-4 w-4 shrink-0 text-red-600' />
              <span>{errorMessage}</span>
            </AlertDescription>
          </Alert>
        )}

        {mode === 'replay' && replayRows && replayRows.length > 0 && (
          <Alert
            className={
              replayHadError
                ? 'border-red-200 bg-red-50'
                : 'border-green-200 bg-green-50'
            }
          >
            <AlertDescription
              className={
                replayHadError
                  ? 'flex items-start gap-2 text-sm text-red-900'
                  : 'flex items-start gap-2 text-sm text-green-900'
              }
            >
              {replayHadError ? (
                <XCircle className='h-4 w-4 shrink-0 text-red-600' />
              ) : (
                <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
              )}
              <span>
                {replayHadError
                  ? `Started ${startedCount} of ${replayRows.length} ${plural(replayRows.length, 'replay', 'replays')}. Review the rows below.`
                  : `Started ${startedCount} ${plural(startedCount, 'replay', 'replays')}. Press Preflight again to read the result.`}
              </span>
            </AlertDescription>
          </Alert>
        )}

        {mode === 'preflight' && preflightRows && preflightRows.length > 0 && (
          <Table
            containerClassName='overflow-x-auto rounded-md border'
            className='min-w-[720px]'
          >
            <TableHeader>
              <TableRow>
                <TableHead className='min-w-[220px]'>Transaction ID</TableHead>
                <TableHead className='w-[80px]'>Type</TableHead>
                <TableHead className='w-[110px]'>Event status</TableHead>
                <TableHead className='w-[80px] text-right'>Stored</TableHead>
                <TableHead>Verdict</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preflightRows.map(row => (
                <TableRow key={row.transaction_id}>
                  <TableCell className='font-mono text-xs'>
                    {row.transaction_id}
                  </TableCell>
                  <TableCell>{row.transaction_set_identifier ?? '—'}</TableCell>
                  <TableCell>{row.status ?? '—'}</TableCell>
                  <TableCell className='text-right'>
                    {row.stored_remittance_count ?? 0}
                  </TableCell>
                  <TableCell>
                    {/* items-start: a flex column stretches its children, which
                        makes the badge span the whole column. */}
                    <div className='flex flex-col items-start gap-1'>
                      <VerdictBadge verdict={row.verdict} />
                      <span className='text-xs text-muted-foreground'>
                        {row.verdict === 'not_835' && row.reason
                          ? row.reason
                          : VERDICT_SENTENCES[row.verdict]}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {mode === 'replay' && replayRows && replayRows.length > 0 && (
          <Table
            containerClassName='overflow-x-auto rounded-md border'
            className='min-w-[720px]'
          >
            <TableHeader>
              <TableRow>
                <TableHead className='min-w-[220px]'>Transaction ID</TableHead>
                <TableHead className='w-[80px]'>Type</TableHead>
                <TableHead className='w-[110px]'>Outcome</TableHead>
                <TableHead className='min-w-[220px]'>Workflow ID</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {replayRows.map(row => (
                <TableRow key={row.transaction_id}>
                  <TableCell className='font-mono text-xs'>
                    {row.transaction_id}
                  </TableCell>
                  <TableCell>{row.transaction_set_identifier ?? '—'}</TableCell>
                  <TableCell>
                    <OutcomeBadge outcome={row.outcome} />
                  </TableCell>
                  <TableCell className='font-mono text-xs break-all'>
                    {row.workflow_id ?? '—'}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>
                    {row.message ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ConfirmReplayDialog
        open={confirmOpen}
        busy={busy}
        count={replayableIds.length}
        typeBreakdown={typeBreakdown}
        alreadyStoredCount={alreadyStoredSelectedCount}
        inFlightCount={inFlightSelectedCount}
        stageLabel={stageLabel}
        // Always closable. The confirm button is disabled while busy, so
        // closing cannot double-submit -- but refusing to close strands the
        // operator with no exit but a page reload if the request never settles.
        onOpenChange={setConfirmOpen}
        onConfirm={() => void runReplay()}
      />
    </section>
  );
};

export default StediWebhookReplayPanel;
